import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Phone, ChevronDown, ChevronUp, Building2, Factory, Warehouse } from "lucide-react";

const serviceData = {
  "cuplock-scaffolding": {
    name: "Cuplock Scaffolding",
    title: "Cuplock Scaffolding System - Rental & Supply | Amar Preet",
    metaDescription: "High-quality cuplock scaffolding for high-rise, commercial & industrial projects. Easy assembly, flexible configurations. Available across Delhi NCR, Hyderabad.",
    h1: "Cuplock Scaffolding - Versatile, Safe, Fast Assembly",
    heroImage: "/images/assets/cuplock-service.PNG",
    intro: "Cuplock scaffolding is a modular system that uses a unique cup-and-blade locking mechanism for quick, secure connections. It's the preferred choice for high-rise construction and complex structures across India.",
    features: [
      "High load capacity up to 20 kN per leg",
      "Quick assembly with minimal components",
      "Flexible configurations for any structure",
      "Positive locking mechanism for safety",
      "Reduced labor and assembly time",
      "Suitable for complex architectural designs"
    ],
    applications: [
      "High-rise residential towers",
      "Commercial building construction",
      "Bridge and flyover construction",
      "Industrial plant maintenance",
      "Stadium and large structure scaffolding"
    ],
    specs: [
      { label: "Load Capacity", value: "Up to 20 kN per leg" },
      { label: "Standard Lengths", value: "0.5m to 3.0m" },
      { label: "Material", value: "High-grade steel" },
      { label: "Finish", value: "Hot-dip galvanized / Painted" }
    ]
  },
  "acrow-span-props": {
    name: "Acrospan Props System",
    title: "Acrospan System - Formwork Props & Slab Support | Amar Preet",
    metaDescription: "Advanced Acrospan formwork system for concrete structures. Rapid installation, reusable, high precision. Available for rent across India.",
    h1: "Acrospan System - Advanced Formwork Solution",
    heroImage: "/images/assets/acrospan-service.JPG",
    intro: "The Acrospan system is an advanced formwork solution designed for efficient slab support and concrete structure construction. It offers rapid installation, high load capacity, and excellent surface finish.",
    features: [
      "Rapid installation and removal",
      "High load bearing capacity",
      "Reusable components",
      "Precise leveling adjustability",
      "Minimal floor preparation needed",
      "Cost-effective for large areas"
    ],
    applications: [
      "Slab formwork support",
      "Multi-story construction",
      "Commercial building slabs",
      "Industrial floor casting",
      "Parking structure construction"
    ],
    specs: [
      { label: "Height Range", value: "1.8m to 5.0m adjustable" },
      { label: "Load Capacity", value: "High capacity system" },
      { label: "Components", value: "Props, heads, stringers" },
      { label: "Application", value: "Slab support up to 20cm" }
    ]
  },
  "industrial-scaffolding": {
    name: "Industrial Scaffolding",
    title: "Industrial Scaffolding Solutions - Plants & Refineries | Amar Preet",
    metaDescription: "Heavy-duty industrial scaffolding for plants, refineries, and manufacturing facilities. Safety compliant, custom configurations. Expert support.",
    h1: "Industrial Scaffolding - Heavy-Duty Solutions",
    heroImage: "/images/assets/industrial-service.jpeg",
    intro: "Industrial scaffolding solutions designed for the demanding requirements of manufacturing plants, refineries, power plants, and chemical facilities. Our systems meet stringent safety standards required in industrial environments.",
    features: [
      "Heavy-duty load capacity",
      "Safety compliant for industrial use",
      "Custom configurations available",
      "Corrosion-resistant options",
      "Suitable for confined spaces",
      "Plant shutdown/turnaround support"
    ],
    applications: [
      "Refinery maintenance",
      "Cement plant scaffolding",
      "Power plant maintenance",
      "Chemical plant access",
      "Steel plant scaffolding",
      "Shipyard construction"
    ],
    specs: [
      { label: "Type", value: "Heavy-duty systems" },
      { label: "Compliance", value: "Industrial safety standards" },
      { label: "Materials", value: "Galvanized / treated steel" },
      { label: "Configuration", value: "Custom per site requirements" }
    ]
  },
  "high-rise": {
    name: "High-Rise Scaffolding",
    title: "Scaffolding for High-Rise Projects - Towers & Buildings | Amar Preet",
    metaDescription: "Specialized scaffolding solutions for high-rise construction. Safe, stable systems for towers 20+ floors. Delhi NCR, Hyderabad, Vizag.",
    h1: "Scaffolding for High-Rise Projects",
    heroImage: "/images/assets/high-rise-project.jpeg",
    intro: "High-rise construction requires specialized scaffolding solutions that ensure safety and stability at great heights. Our cuplock and system scaffolding are designed for multi-story towers and skyscrapers.",
    features: [
      "Stable at heights of 50+ floors",
      "Wind load resistant design",
      "Multiple working level support",
      "Safety net and toe board compatible",
      "Easy vertical extension",
      "Compliant with height safety standards"
    ],
    applications: [
      "Residential tower construction",
      "Commercial high-rise buildings",
      "Hotel and hospitality projects",
      "Mixed-use developments",
      "High-rise maintenance work"
    ],
    specs: [
      { label: "Max Height", value: "Suitable for 50+ floors" },
      { label: "System", value: "Cuplock modular" },
      { label: "Safety", value: "Net & toe board provision" },
      { label: "Configuration", value: "Per building design" }
    ]
  }
};

export default function ServiceDetailPage({ service: serviceProp }) {
  const { service: serviceParam } = useParams();
  const serviceKey = serviceProp || serviceParam;
  const data = serviceData[serviceKey];
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (data) {
      document.title = data.title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute("content", data.metaDescription);
      }
    }
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-secondary mb-4">Service Not Found</h1>
          <Link to="/services" className="text-primary hover:underline">View All Services</Link>
        </div>
      </div>
    );
  }

  const faqs = [
    {
      question: `What is ${data.name}?`,
      answer: data.intro
    },
    {
      question: `What are the main features of ${data.name}?`,
      answer: `Key features include: ${data.features.slice(0, 4).join(", ")}. Contact us for detailed specifications.`
    },
    {
      question: `Is ${data.name} available for rent?`,
      answer: `Yes, ${data.name} is available for rent across our service areas including Delhi NCR, Hyderabad, and Vizag. We offer flexible rental terms and competitive pricing.`
    },
    {
      question: "Do you provide installation support?",
      answer: "We do not provide installation support. We only supply the material on rent. Your team or contractor handles the erection and dismantling on site."
    }
  ];

  return (
    <div data-testid={`service-page-${serviceKey}`}>
      {/* Hero */}
      <section className="bg-secondary py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <nav className="mb-6 text-slate-400 text-sm">
                <Link to="/" className="hover:text-primary">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/services" className="hover:text-primary">Services</Link>
                <span className="mx-2">/</span>
                <span className="text-primary">{data.name}</span>
              </nav>
              <h1 className="font-heading text-4xl md:text-5xl font-black text-white uppercase tracking-tighter leading-tight mb-6" data-testid="service-headline">
                {data.h1}
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                {data.intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-primary text-primary-foreground font-bold uppercase tracking-wider px-8 py-4 hover:bg-primary/90 transition-all border-b-4 border-yellow-600 text-center"
                  data-testid="service-cta"
                >
                  Get Quote
                </Link>
                <a
                  href="tel:+919355744699"
                  className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:text-secondary transition-all text-center flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
              </div>
            </div>
            <div>
              <img
                src={data.heroImage}
                alt={`${data.name} for construction projects`}
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary uppercase tracking-tight mb-6">
                Key Features
              </h2>
              <div className="space-y-4">
                {data.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-muted p-8">
              <h3 className="font-heading text-xl font-bold text-secondary uppercase tracking-tight mb-6">
                Specifications
              </h3>
              <div className="space-y-4">
                {data.specs.map((spec) => (
                  <div key={spec.label} className="flex justify-between border-b border-border pb-3">
                    <span className="text-muted-foreground">{spec.label}</span>
                    <span className="font-bold text-secondary">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary uppercase tracking-tight mb-4">
              Applications
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {data.name} is ideal for various construction and industrial applications
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.applications.map((app) => (
              <div key={app} className="bg-white p-4 text-center border border-border">
                <span className="text-sm text-secondary">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-6">
                Why Choose Amar Preet for {data.name}?
              </h2>
              <div className="space-y-4">
                {[
                  "30+ years of industry experience",
                  "ISO 9001:2015 certified equipment",
                  "Multi-city inventory for quick delivery",
                  "Technical support and guidance",
                  "Competitive rental pricing",
                  "Flexible rental terms"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-primary-foreground uppercase tracking-tight mb-4">
                Need {data.name}?
              </h3>
              <p className="text-primary-foreground/80 mb-6">
                Get a customized quote for your project
              </p>
              <Link
                to="/contact"
                className="inline-block bg-secondary text-secondary-foreground font-bold uppercase tracking-wider px-8 py-4 hover:bg-secondary/90 transition-all"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary uppercase tracking-tight mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-border bg-white" data-testid={`faq-${index}`}>
                <button
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  <span className="font-bold text-secondary pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h3 className="font-heading text-xl font-bold text-secondary uppercase tracking-tight mb-6 text-center">
            Related Services
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(serviceData)
              .filter(([key]) => key !== serviceKey)
              .slice(0, 3)
              .map(([key, value]) => (
                <Link
                  key={key}
                  to={`/${key}`}
                  className="px-6 py-3 bg-white border border-border hover:border-primary transition-colors"
                >
                  {value.name}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
